function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { Box } from 'features/fabric/components/Box';
import { List } from 'features/fabric/components/List';
import { Logo } from 'features/fabric/components/Logo';
import { Responsive } from 'features/fabric/components/Responsive';
import { ResponsiveProvider } from 'features/fabric/components/ResponsiveProvider';
import { SocialFacebookIcon, SocialLinkedInIcon } from 'features/fabric/components/SocialIcon';
import useResponsive from 'features/fabric/hooks/useResponsive';
import RichTextMarkdown from 'features/richText/components/richTextMarkdown/RichTextMarkdown';
import { getHideContent } from 'features/searchCompare/state/menu';
import { buildLinkProps } from 'features/searchCompare/utils/footerUtils';
import { useTextPlaceholders } from 'features/textPlaceholders/useTextPlaceholders';
import { withTextPlaceholders } from 'features/textPlaceholders/withTextPlaceholders';
import { getSiteSetting } from 'framework/globalStorage/settingsStore';
import { useAppSelector } from 'framework/store/hooks';
import { getCountryCode, getLocale } from 'framework/utils/core/siteUtils';
import { useTranslate } from 'framework/utils/i18n/useTranslate';
import { Fragment, use } from 'react';
import { useLocation } from 'react-router';
import Footer from './components/Footer';
import styles from './FooterContainer.scss';
import { footerQuery } from './footerQuery';
import getInitialProps from './getInitialProps';
const MOBILE_PL_MENU_HEIGHT = 73;
const COPYRIGHT_YEAR = new Date().getFullYear();
const isInvalid = (link)=>!(link === null || link === void 0 ? void 0 : link.url);
const getSocialIcon = (name)=>{
    const socialIconProps = {
        size: 's',
        background: 'primary-action-base',
        hoverBackground: 'grayscale0',
        color: 'grayscale0',
        hoverColor: 'primary-action-base'
    };
    if (name === 'facebook-outlined') {
        return /*#__PURE__*/ _jsx(SocialFacebookIcon, _object_spread({}, socialIconProps));
    } else if (name === 'linkedin-outlined') {
        return /*#__PURE__*/ _jsx(SocialLinkedInIcon, _object_spread({}, socialIconProps));
    }
    return null;
};
const useFooter = ()=>{
    const countryCode = getCountryCode();
    const locale = getLocale();
    const textPlaceholders = useTextPlaceholders();
    const { data: footerData } = useQuery(footerQuery(countryCode, locale));
    return withTextPlaceholders(footerData, textPlaceholders);
};
const FooterContainer = ()=>{
    var _location_pathname;
    const translate = useTranslate();
    const location = useLocation();
    const countryCode = getCountryCode();
    const hideContent = useAppSelector(getHideContent);
    const breakpoints = use(ResponsiveProvider.Context);
    const footer = useFooter();
    // We don't check isLoading or isError here, we just always render
    // the fallback footer when footerData is not available for any reason
    const headings = footer === null || footer === void 0 ? void 0 : footer.headings;
    const contentLinks = footer === null || footer === void 0 ? void 0 : footer.contentLinks;
    const socialLinks = footer === null || footer === void 0 ? void 0 : footer.socialLinks;
    const countryLinks = footer === null || footer === void 0 ? void 0 : footer.countryLinks;
    const copyrightAddition = translate('PR.Footer.CopyrightAddition');
    const footerDescriptionKey = `PR.Footer.Description.${countryCode}`;
    const copyrightText = `Copyright 1999-${COPYRIGHT_YEAR} Klarna Technologies AB ${copyrightAddition ? `\n ${copyrightAddition}` : ''}`;
    const mainAreaColumns = useResponsive({
        base: 12,
        l: 10
    });
    const firstColumnSpan = useResponsive({
        base: 12,
        l: 4
    });
    const restColumnsSpan = useResponsive({
        base: 12,
        m: 4,
        l: 2
    });
    const footerWrapperClasses = classNames(styles.footerWrapper, {
        [styles.hide]: hideContent
    });
    if (!footer) {
        const supportEmail = getSiteSetting('support_email');
        return /*#__PURE__*/ _jsx("div", {
            className: footerWrapperClasses,
            children: /*#__PURE__*/ _jsx(Footer, {
                children: /*#__PURE__*/ _jsx(Footer.Bottom, {
                    copyright: copyrightText,
                    children: /*#__PURE__*/ _jsx(Footer.Link, {
                        to: `mailto:${supportEmail}`,
                        children: supportEmail
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: footerWrapperClasses,
        children: /*#__PURE__*/ _jsxs(Footer, {
            children: [
                /*#__PURE__*/ _jsxs(Footer.Main, {
                    columns: mainAreaColumns,
                    children: [
                        contentLinks === null || contentLinks === void 0 ? void 0 : contentLinks.map((item, index)=>{
                            var _headings_index;
                            if (!item) {
                                return null;
                            }
                            const heading = (headings === null || headings === void 0 ? void 0 : (_headings_index = headings[index]) === null || _headings_index === void 0 ? void 0 : _headings_index.content) || '';
                            const list = item === null || item === void 0 ? void 0 : item.list;
                            return /*#__PURE__*/ _jsx(Footer.Column, {
                                heading: heading,
                                span: restColumnsSpan,
                                sx: {
                                    order: !breakpoints.l ? 1 : 2
                                },
                                children: list && /*#__PURE__*/ _jsx(List, {
                                    kind: "plain",
                                    children: list.map((link)=>{
                                        if (!link || isInvalid(link)) {
                                            return null;
                                        }
                                        const linkProps = buildLinkProps(link);
                                        const linkTitle = link === null || link === void 0 ? void 0 : link.title;
                                        return /*#__PURE__*/ _jsx(List.Item, {
                                            children: /*#__PURE__*/ _jsx(Footer.Link, _object_spread_props(_object_spread({}, linkProps), {
                                                children: linkTitle
                                            }))
                                        }, `key-${linkTitle}`);
                                    })
                                })
                            }, item.id);
                        }),
                        /*#__PURE__*/ _jsx(Footer.Column, {
                            span: firstColumnSpan,
                            sx: {
                                order: !breakpoints.l ? 4 : 1
                            },
                            children: /*#__PURE__*/ _jsxs(Box, {
                                paddingRight: {
                                    base: 'none',
                                    m: 'xxl'
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        paddingBottom: {
                                            base: 'm',
                                            m: 'l'
                                        },
                                        children: /*#__PURE__*/ _jsx(Logo, {
                                            color: "text-inverse",
                                            width: 240
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextMarkdown, {
                                        source: translate(footerDescriptionKey)
                                    }),
                                    countryLinks && countryLinks.map((country, index)=>{
                                        if (!country) {
                                            return null;
                                        }
                                        const countryName = country === null || country === void 0 ? void 0 : country.title;
                                        const linkProps = buildLinkProps(country);
                                        return /*#__PURE__*/ _jsxs(Fragment, {
                                            children: [
                                                index !== 0 && /*#__PURE__*/ _jsx("span", {
                                                    children: ", "
                                                }),
                                                /*#__PURE__*/ _jsx(Footer.Link, _object_spread_props(_object_spread({}, linkProps), {
                                                    children: countryName
                                                }))
                                            ]
                                        }, `key-${countryName}`);
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Footer.Bottom, {
                    copyright: copyrightText,
                    children: socialLinks && socialLinks.map((link)=>{
                        if (isInvalid(link)) {
                            return null;
                        }
                        const linkProps = buildLinkProps(link);
                        const linkTitle = link === null || link === void 0 ? void 0 : link.title;
                        let ariaLabel = '';
                        if (linkTitle === 'facebook-outlined') {
                            ariaLabel = translate('PR.Footer.GO_TO_FACEBOOK');
                        } else if (linkTitle === 'linkedin-outlined') {
                            ariaLabel = translate('PR.Footer.GO_TO_LINKEDIN');
                        }
                        return /*#__PURE__*/ _jsx(Footer.Link, _object_spread_props(_object_spread({}, linkProps), {
                            "aria-label": ariaLabel,
                            children: getSocialIcon(linkTitle)
                        }), `key-${linkTitle}`);
                    })
                }),
                (location === null || location === void 0 ? void 0 : (_location_pathname = location.pathname) === null || _location_pathname === void 0 ? void 0 : _location_pathname.startsWith('/pl')) && /*#__PURE__*/ _jsx(Responsive, {
                    mobile: true,
                    tablet: true,
                    children: /*#__PURE__*/ _jsx("div", {
                        style: {
                            paddingBottom: MOBILE_PL_MENU_HEIGHT
                        }
                    })
                })
            ]
        })
    });
};
FooterContainer.getInitialProps = getInitialProps;
export default FooterContainer;
